import classnames from "classnames";
import { inject } from "mobx-react";
import React, { Component, SyntheticEvent } from "react";
import { Translation } from "react-i18next";
import { resizeWatcher } from "../../lib/resize-watcher";
import { Button } from "../../ui";
import type { Store } from "../../../../client/stores";
import "./ReadMore.style.scss";
import { insertAds } from "./ad-inserter";
import { AdPosition } from "../../page-blocks/Ad.component";
import { ItemBlockValue } from "../../decoders/Sites.types";
import { ButtonKind } from "../../ui/Button.component";

interface ReadMoreState {
    displayRM: boolean;
    height: number | null;
}

interface ReadMoreProps {
    store?: Store;
    tags?: string[] | null;
    blockValue?: ItemBlockValue | null;
}

@inject("store")
export class ReadMore extends Component<ReadMoreProps, ReadMoreState> {
    private minimumWidth = 760;
    private contentRef: React.RefObject<HTMLDivElement>;
    private minHeight = 600; // button should always initially reveal the first 600px of content
    mounted = false;

    constructor(props: ReadMoreProps) {
        super(props);

        this.state = {
            displayRM: false,
            height: null,
        };

        // to get the height and offset
        this.contentRef = React.createRef();
    }

    componentDidMount() {
        this.mounted = true;
        // insert the ad before we know if we need to show readmore so readmore knows where the ad is
        if (this.props.store) {
            insertAds({
                contentDetailContainer: this.contentRef.current?.querySelector(".content-detail-container"),
                tags: this.props.tags,
                adId: this.props.blockValue?.position,
                adSplit: this.props.blockValue?.ad_split,
                adElement: AdPosition,
                store: this.props.store,
            });
        }

        // TODO: Disabling this as part of IHRAL-7181 but leaving it here because IHRAL-7188 may require its resurrection
        /*
        if (window.innerWidth <= this.minimumWidth) {
            this.show();
        }
        resizeWatcher.onWidthChange.subscribe(this.resizeHandler);
        */
    }

    componentWillUnmount() {
        this.mounted = false;
        resizeWatcher.onWidthChange.unsubscribe(this.resizeHandler);
    }

    private resizeHandler = () => {
        if (window.innerWidth <= this.minimumWidth) {
            this.show();
        }
        if (window.innerWidth > this.minimumWidth) {
            this.cleanUp();
        }
    };

    show = () => {
        if (!this.contentRef.current) {
            return;
        }
        const { current } = this.contentRef;
        let displayRM = false;

        const windowHeight = window.innerHeight;
        let height = windowHeight; // initialize
        const contentHeight = current.clientHeight;
        const contentHeightPercentage = Math.floor((contentHeight / windowHeight) * 100);

        // content height is at least 150 - 200% taller than screen height,
        if (contentHeightPercentage > 150) {
            displayRM = true;
            const buttonStart = windowHeight * 1.25;

            // get bottom of ad relative to content
            const ad = current.querySelector(".component-ad-unit") as HTMLElement;
            let adBottom = 0;
            if (ad) {
                adBottom = ad.offsetTop + ad.offsetHeight;
            }

            if (adBottom > buttonStart) {
                // if adbottom is lower, use that so ad is still visible
                height = adBottom;
            } else if (adBottom < buttonStart) {
                // if buttonStart is lower,
                height = buttonStart;
            }
        }

        if (this.props.store?.request.hash) {
            displayRM = false;
        }

        if (this.mounted && displayRM) {
            this.setState({
                displayRM: true,
                height: height < this.minHeight ? this.minHeight : height,
            });
        }
    };

    cleanUp = (event?: SyntheticEvent<Element, Event>) => {
        if (!this.contentRef.current) {
            return;
        }
        if (this.mounted) {
            this.setState({
                displayRM: false,
                height: null,
            });
        }
        if (this.props.store && event) {
            this.props.store.onAnalyticsAction.dispatch({
                sectionName: "body",
                pageName: this.props.store.page.currentPage
                    ? `${this.props.store.microsite ? "microsite_" : ""}${this.props.store.page.currentPage.name}`
                    : "",
                context: "continue_reading",
                action: "click",
            });
        }
    };

    render() {
        const style: { height?: string } = {};
        if (this.state.height) {
            style.height = `${this.state.height}px`;
        }
        const divClass = classnames("component-readmore", {
            active: this.state.displayRM,
        });

        return (
            <div className={divClass} ref={this.contentRef} style={style}>
                {this.props.children}
                {this.state.displayRM ? (
                    <div className="read-more-button">
                        <Translation>
                            {t => (
                                <Button kind={ButtonKind.PRIMARY} click={this.cleanUp}>
                                    {t("cont_read")}
                                </Button>
                            )}
                        </Translation>
                    </div>
                ) : null}
            </div>
        );
    }
}
